import Datetime from 'react-datetime';
import GridItem from 'components/Grid/GridItem';
import CustomInput from 'components/CustomInput/CustomInput';
import GridContainer from 'components/Grid/GridContainer';
import { useTranslation } from 'react-i18next';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import CardWrapper from 'components/Card/CardWrapper';
import CardBody from 'components/Card/CardBody';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import makeStyles from '@material-ui/styles/makeStyles';

const CONSENT_RESTRICTIONS = [
  {
    field: 'requiredEmailConsent',
    type: 'EMAIL',
    labelKey: 'discountCodes.requiredEmailConsent',
  },
  {
    field: 'requiredSmsConsent',
    type: 'SMS',
    labelKey: 'discountCodes.requiredSmsConsent',
  },
  {
    field: 'requiredPhoneConsent',
    type: 'PHONE',
    labelKey: 'discountCodes.requiredPhoneConsent',
  },
];

const stopLabelToggle = e => {
  e.preventDefault();
  e.stopPropagation();
};

const useStyles = makeStyles({
  restrictionsAdminSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  onlyFirstOrderCheckbox: {
    marginTop: '10px',
  },
  dateTo: {
    paddingBottom: '0px',
  },
});

const RestrictionsSection = ({
  startDateValidation,
  endDateValidation,
  restrictionsSection,
  setRestrictionsSection,
  isEdit,
  brandConsentsAvailability,
}) => {
  const { t } = useTranslation();

  const classes = useStyles();

  return (
    <CardWrapper title={t('discountCodes.restrictions')}>
      <CardBody>
        <GridContainer>
          <GridItem sm={6} className={classes.restrictionsAdminSection}>
            <LabeledCheckbox
              className={classes.onlyFirstOrderCheckbox}
              checked={restrictionsSection.onlyFirstOrder}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  onlyFirstOrder: e.target.checked,
                }));
              }}
              label={t('discountCodes.onlyFirstOrder')}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.isStartDate}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isStartDate: e.target.checked,
                  startDate: null,
                }));
              }}
              label={t('discountCodes.activeFrom')}
            />
            <Datetime
              timeFormat={true}
              isValidDate={startDateValidation}
              value={
                restrictionsSection.isStartDate
                  ? restrictionsSection.startDate
                  : null
              }
              onFocus={() => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isStartDate: true,
                }));
              }}
              onChange={startDate => {
                setRestrictionsSection(prev => ({ ...prev, startDate }));
              }}
              inputProps={{ readOnly: true }}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.isLimit}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isLimit: e.target.checked,
                  limit: '',
                }));
              }}
              label={t('discountCodes.usageLimit')}
            />
            <CustomInput
              formControlProps={{ fullWidth: true }}
              inputProps={{
                name: 'limit',
                value: restrictionsSection.isLimit
                  ? restrictionsSection.limit
                  : '',
                onFocus: () => {
                  setRestrictionsSection(prev => ({
                    ...prev,
                    isLimit: true,
                  }));
                },
                onChange: e => {
                  if (!isNaN(e.target.value)) {
                    setRestrictionsSection(prev => ({
                      ...prev,
                      limit: e.target.value,
                    }));
                  }
                },
              }}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.isEndDate}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isEndDate: e.target.checked,
                  endDate: null,
                }));
              }}
              label={t('discountCodes.activeTo')}
            />
            <Datetime
              className={classes.dateTo}
              timeFormat={true}
              value={
                // eslint-disable-next-line no-negated-condition -- FIXME
                !restrictionsSection.isEndDate
                  ? null
                  : restrictionsSection.endDate
              }
              isValidDate={endDateValidation}
              onFocus={() => {
                setRestrictionsSection(prev => ({ ...prev, isEndDate: true }));
              }}
              onChange={endDate => {
                setRestrictionsSection(prev => ({ ...prev, endDate }));
              }}
              inputProps={{ readOnly: true }}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.isLimitPerUser}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isLimitPerUser: e.target.checked,
                }));
              }}
              label={t('discountCodes.usageLimitPerUser')}
              disabled={isEdit}
            />

            <CustomInput
              formControlProps={{ fullWidth: true }}
              inputProps={{
                name: 'limitPerUser',
                value: restrictionsSection.isLimitPerUser
                  ? restrictionsSection.limitPerUser
                  : '',
                onFocus: () => {
                  setRestrictionsSection(prev => ({
                    ...prev,
                    isLimitPerUser: true,
                  }));
                },
                onChange: e => {
                  if (!isNaN(e.target.value)) {
                    setRestrictionsSection(prev => ({
                      ...prev,
                      limitPerUser: e.target.value,
                    }));
                  }
                },
              }}
              disabled={isEdit}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.isMinimalOrderValue}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isMinimalOrderValue: e.target.checked,
                }));
              }}
              label={t('discountCodes.minimalOrderValue')}
              disabled={isEdit}
            />
            <CustomInput
              formControlProps={{ fullWidth: true }}
              inputProps={{
                name: 'minimalOrderValue',
                value: restrictionsSection.isMinimalOrderValue
                  ? restrictionsSection.minimalOrderValue
                  : '',
                onFocus: () => {
                  setRestrictionsSection(prev => ({
                    ...prev,
                    isMinimalOrderValue: true,
                  }));
                },
                onChange: e => {
                  if (!isNaN(e.target.value)) {
                    setRestrictionsSection(prev => ({
                      ...prev,
                      minimalOrderValue: e.target.value,
                    }));
                  }
                },
              }}
              disabled={isEdit}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.isLimitPerSubscription}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isLimitPerSubscription: e.target.checked,
                }));
              }}
              label={t('discountCodes.usageLimitPerSubscription')}
              disabled={isEdit}
            />
            <CustomInput
              formControlProps={{ fullWidth: true }}
              inputProps={{
                name: 'limitPerSubscription',
                value: restrictionsSection.isLimitPerSubscription
                  ? restrictionsSection.limitPerSubscription
                  : '',
                onFocus: () => {
                  setRestrictionsSection(prev => ({
                    ...prev,
                    isLimitPerSubscription: true,
                  }));
                },
                onChange: e => {
                  if (!isNaN(e.target.value)) {
                    setRestrictionsSection(prev => ({
                      ...prev,
                      limitPerSubscription: e.target.value,
                    }));
                  }
                },
              }}
              disabled={isEdit}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.isMinimalDietLength}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  isMinimalDietLength: e.target.checked,
                }));
              }}
              label={t('discountCodes.minimalDietLength')}
              disabled={isEdit}
            />
            <CustomInput
              formControlProps={{ fullWidth: true }}
              inputProps={{
                name: 'minimalDietLength',
                value: restrictionsSection.isMinimalDietLength
                  ? restrictionsSection.minimalDietLength
                  : '',
                onFocus: () => {
                  setRestrictionsSection(prev => ({
                    ...prev,
                    isMinimalDietLength: true,
                  }));
                },
                onChange: e => {
                  if (!isNaN(e.target.value)) {
                    setRestrictionsSection(prev => ({
                      ...prev,
                      minimalDietLength: e.target.value,
                    }));
                  }
                },
              }}
              disabled={isEdit}
            />
          </GridItem>
          <GridItem sm={6}>
            <LabeledCheckbox
              checked={restrictionsSection.disableMoneybox}
              onChange={e => {
                setRestrictionsSection(prev => ({
                  ...prev,
                  disableMoneybox: e.target.checked,
                }));
              }}
              label={t('discountCodes.notCombinePiggyBank.label')}
              disabled={isEdit}
            />
          </GridItem>
          {CONSENT_RESTRICTIONS.map(({ field, type, labelKey }) => {
            const isAvailable = brandConsentsAvailability[type];

            return (
              <GridItem sm={6} key={field}>
                <LabeledCheckbox
                  checked={restrictionsSection[field]}
                  onChange={e => {
                    setRestrictionsSection(prev => ({
                      ...prev,
                      [field]: e.target.checked,
                    }));
                  }}
                  label={
                    isAvailable ? (
                      t(labelKey)
                    ) : (
                      <>
                        {t(labelKey)}
                        <span onClick={stopLabelToggle}>
                          <InfoIconTooltip
                            title={t('discountCodes.requiredConsent.disabled')}
                          />
                        </span>
                      </>
                    )
                  }
                  disabled={!isAvailable && !restrictionsSection[field]}
                />
              </GridItem>
            );
          })}
        </GridContainer>
      </CardBody>
    </CardWrapper>
  );
};

export default RestrictionsSection;
